"use client"

import { useState, useEffect } from "react"
import { 
  Layers, Plus, Edit3, Trash2, X, Save, Search, ChevronRight, ChevronDown,
  AlertCircle, Grid
} from 'lucide-react'
import { authFetch } from "../utils/authFetch";

const GestionCategorias = ({ API_URL, onClose, token }) => {
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategorias, setExpandedCategorias] = useState([]);
  
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [showSubcategoriaModal, setShowSubcategoriaModal] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [editingSubcategoria, setEditingSubcategoria] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  const [categoriaFormData, setCategoriaFormData] = useState({ nombre: "" });
  const [subcategoriaFormData, setSubcategoriaFormData] = useState({ nombre: "", categoria_id: "" });

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    if (token) loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadCategorias(), loadSubcategorias()]);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategorias = async () => {
    try {
      const response = await authFetch(`${API_URL}/categorias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) setCategorias(result.data);
    } catch (err) {
      console.error('Error loading categorias:', err);
    }
  };

  const loadSubcategorias = async () => {
    try {
      const response = await authFetch(`${API_URL}/subcategorias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) setSubcategorias(result.data);
    } catch (err) {
      console.error('Error loading subcategorias:', err);
    }
  };

  const toggleCategoria = (categoriaId) => {
    setExpandedCategorias(prev =>
      prev.includes(categoriaId) ? prev.filter(id => id !== categoriaId) : [...prev, categoriaId]
    );
  };

  const handleOpenCategoriaModal = (categoria = null) => {
    if (categoria) {
      setEditingCategoria(categoria);
      setCategoriaFormData({ nombre: categoria.nombre });
    } else {
      setEditingCategoria(null);
      setCategoriaFormData({ nombre: "" });
    }
    setShowCategoriaModal(true);
  };

  const handleOpenSubcategoriaModal = (subcategoria = null, categoriaId = null) => {
    if (subcategoria) {
      setEditingSubcategoria(subcategoria);
      setSubcategoriaFormData({ nombre: subcategoria.nombre, categoria_id: subcategoria.categoria_id });
    } else {
      setEditingSubcategoria(null);
      setSubcategoriaFormData({ nombre: "", categoria_id: categoriaId || "" });
    }
    setShowSubcategoriaModal(true);
  };

  const handleCloseCategoriaModal = () => {
    setShowCategoriaModal(false);
    setEditingCategoria(null);
    setCategoriaFormData({ nombre: "" });
  };

  const handleCloseSubcategoriaModal = () => {
    setShowSubcategoriaModal(false);
    setEditingSubcategoria(null);
    setSubcategoriaFormData({ nombre: "", categoria_id: "" });
  };

  const handleSaveCategoria = async () => {
    if (!categoriaFormData.nombre.trim()) {
      alert('El nombre de la categoría es requerido');
      return;
    }
    try {
      const url = editingCategoria ? `${API_URL}/categorias/${editingCategoria.id}` : `${API_URL}/categorias`;
      const method = editingCategoria ? 'PUT' : 'POST';
      const response = await authFetch(url, { method, headers: authHeaders, body: JSON.stringify(categoriaFormData) });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(editingCategoria ? '✅ Categoría actualizada' : '✅ Categoría creada');
        await loadCategorias();
        handleCloseCategoriaModal();
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error saving categoria:', err);
      alert('❌ Error al guardar categoría');
    }
  };

  const handleSaveSubcategoria = async () => {
    if (!subcategoriaFormData.nombre.trim() || !subcategoriaFormData.categoria_id) {
      alert('Complete todos los campos');
      return;
    }
    try {
      const url = editingSubcategoria ? `${API_URL}/subcategorias/${editingSubcategoria.id}` : `${API_URL}/subcategorias`;
      const method = editingSubcategoria ? 'PUT' : 'POST';
      const response = await authFetch(url, { method, headers: authHeaders, body: JSON.stringify(subcategoriaFormData) });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(editingSubcategoria ? '✅ Subcategoría actualizada' : '✅ Subcategoría creada');
        await loadSubcategorias();
        handleCloseSubcategoriaModal();
        if (!expandedCategorias.includes(parseInt(subcategoriaFormData.categoria_id))) {
          toggleCategoria(parseInt(subcategoriaFormData.categoria_id));
        }
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error saving subcategoria:', err);
      alert('❌ Error al guardar subcategoría');
    }
  };

  const handleDelete = async (item, type) => {
    try {
      const url = type === 'categoria' ? `${API_URL}/categorias/${item.id}` : `${API_URL}/subcategorias/${item.id}`;
      const response = await authFetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(`✅ ${type === 'categoria' ? 'Categoría' : 'Subcategoría'} eliminada`);
        type === 'categoria' ? await loadCategorias() : await loadSubcategorias();
        setShowDeleteConfirm(null);
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error deleting:', err);
      alert('❌ Error al eliminar');
    }
  };

  const getSubcategoriasByCategoria = (categoriaId) => subcategorias.filter(s => s.categoria_id === categoriaId);

  const filteredCategorias = categorias.filter(c =>
    c.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="gestion-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando categorías...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestion-container">
      <div className="gestion-header">
        <div className="header-left">
          <Layers size={24} className="header-icon" />
          <div>
            <h2>Gestión de Categorías</h2>
            <p>{categorias.length} categoría{categorias.length !== 1 ? 's' : ''} • {subcategorias.length} subcategoría{subcategorias.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-close" onClick={onClose}><X size={18} /></button>
        </div>
      </div>

      <div className="gestion-toolbar">
        <div className="search-box">
          <Search size={16} />
          <input type="text" placeholder="Buscar categorías..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="toolbar-actions">
          <button className="btn-secondary" onClick={() => handleOpenSubcategoriaModal()}>
            <Plus size={16} /><span>Subcategoría</span>
          </button>
          <button className="btn-primary" onClick={() => handleOpenCategoriaModal()}>
            <Plus size={16} /><span>Categoría</span>
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th width="40"></th>
              <th>Nombre</th>
              <th>Subcategorías</th>
              <th>Productos</th>
              <th width="100">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategorias.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  <AlertCircle size={20} />
                  <span>No se encontraron categorías</span>
                </td>
              </tr>
            ) : (
              filteredCategorias.map((categoria) => {
                const isExpanded = expandedCategorias.includes(categoria.id);
                const catSubcategorias = getSubcategoriasByCategoria(categoria.id);
                return (
                  <>
                    <tr key={categoria.id} className="categoria-row">
                      <td>
                        <button className="expand-btn" onClick={() => toggleCategoria(categoria.id)} disabled={catSubcategorias.length === 0}>
                          {catSubcategorias.length > 0 && (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
                        </button>
                      </td>
                      <td className="font-medium">{categoria.nombre}</td>
                      <td><span className="badge">{catSubcategorias.length}</span></td>
                      <td><span className="badge secondary">{categoria.producto_count || 0}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon add" onClick={() => handleOpenSubcategoriaModal(null, categoria.id)} title="Añadir subcategoría"><Plus size={14} /></button>
                          <button className="btn-icon edit" onClick={() => handleOpenCategoriaModal(categoria)} title="Editar"><Edit3 size={14} /></button>
                          <button className="btn-icon delete" onClick={() => setShowDeleteConfirm({ item: categoria, type: 'categoria' })} title="Eliminar"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && catSubcategorias.map((subcategoria) => (
                      <tr key={`sub-${subcategoria.id}`} className="subcategoria-row">
                        <td></td>
                        <td>
                          <div className="subcategoria-name">
                            <Grid size={14} />
                            <span>{subcategoria.nombre}</span>
                          </div>
                        </td>
                        <td>-</td>
                        <td><span className="badge secondary">{subcategoria.producto_count || 0}</span></td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-icon edit" onClick={() => handleOpenSubcategoriaModal(subcategoria)} title="Editar"><Edit3 size={14} /></button>
                            <button className="btn-icon delete" onClick={() => setShowDeleteConfirm({ item: subcategoria, type: 'subcategoria' })} title="Eliminar"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showCategoriaModal && (
        <div className="modal-overlay" onClick={handleCloseCategoriaModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCategoria ? 'Editar Categoría' : 'Nueva Categoría'}</h3>
              <button onClick={handleCloseCategoriaModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nombre <span className="required">*</span></label>
                <input type="text" placeholder="Nombre de la categoría" value={categoriaFormData.nombre} onChange={(e) => setCategoriaFormData({ nombre: e.target.value })} className="input" autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseCategoriaModal}>Cancelar</button>
              <button className="btn-primary" onClick={handleSaveCategoria} disabled={!categoriaFormData.nombre.trim()}>
                <Save size={16} /><span>{editingCategoria ? 'Actualizar' : 'Crear'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubcategoriaModal && (
        <div className="modal-overlay" onClick={handleCloseSubcategoriaModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingSubcategoria ? 'Editar Subcategoría' : 'Nueva Subcategoría'}</h3>
              <button onClick={handleCloseSubcategoriaModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Categoría <span className="required">*</span></label>
                <select value={subcategoriaFormData.categoria_id} onChange={(e) => setSubcategoriaFormData({ ...subcategoriaFormData, categoria_id: e.target.value })} className="input" disabled={!!editingSubcategoria}>
                  <option value="">Seleccionar categoría</option>
                  {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Nombre <span className="required">*</span></label>
                <input type="text" placeholder="Nombre de la subcategoría" value={subcategoriaFormData.nombre} onChange={(e) => setSubcategoriaFormData({ ...subcategoriaFormData, nombre: e.target.value })} className="input" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseSubcategoriaModal}>Cancelar</button>
              <button className="btn-primary" onClick={handleSaveSubcategoria} disabled={!subcategoriaFormData.nombre.trim() || !subcategoriaFormData.categoria_id}>
                <Save size={16} /><span>{editingSubcategoria ? 'Actualizar' : 'Crear'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-box confirm" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon"><AlertCircle size={48} /></div>
            <h3>¿Eliminar {showDeleteConfirm.type === 'categoria' ? 'Categoría' : 'Subcategoría'}?</h3>
            <p>Esta acción no se puede deshacer. Los productos asociados permanecerán pero sin {showDeleteConfirm.type === 'categoria' ? 'categoría' : 'subcategoría'} asignada.</p>
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(null)}>Cancelar</button>
              <button className="btn-danger" onClick={() => handleDelete(showDeleteConfirm.item, showDeleteConfirm.type)}>
                <Trash2 size={16} /><span>Eliminar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .gestion-container { display: flex; flex-direction: column; height: 100%; background: white; }
        .loading-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 400px; gap: 16px; }
        .spinner { width: 40px; height: 40px; border: 3px solid #E5E7EB; border-top: 3px solid #3B82F6; border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .gestion-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #E5E7EB; }
        .header-left { display: flex; align-items: center; gap: 12px; }
        .header-icon { color: #8B5CF6; }
        .gestion-header h2 { margin: 0; font-size: 20px; font-weight: 600; color: #1A1A1A; }
        .gestion-header p { margin: 2px 0 0 0; font-size: 13px; color: #64748B; }
        .header-actions { display: flex; gap: 8px; }
        .btn-close { padding: 6px; border: none; border-radius: 6px; background: #F1F5F9; color: #64748B; cursor: pointer; transition: all 0.2s; }
        .btn-close:hover { background: #E5E7EB; color: #EF4444; }
        .gestion-toolbar { display: flex; justify-content: space-between; align-items: center; padding: 16px 24px; border-bottom: 1px solid #E5E7EB; }
        .search-box { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 6px; width: 300px; }
        .search-box svg { color: #94A3B8; flex-shrink: 0; }
        .search-box input { flex: 1; border: none; background: none; font-size: 13px; color: #1A1A1A; outline: none; }
        .search-box input::placeholder { color: #94A3B8; }
        .toolbar-actions { display: flex; gap: 8px; }
        .btn-primary { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border: none; border-radius: 6px; background: #8B5CF6; color: white; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .btn-primary:hover { background: #7C3AED; }
        .btn-primary:disabled { background: #CBD5E1; cursor: not-allowed; }
        .btn-secondary { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border: 1px solid #E5E7EB; border-radius: 6px; background: white; color: #64748B; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .btn-secondary:hover { background: #F9FAFB; color: #8B5CF6; }
        .table-container { flex: 1; overflow: auto; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table thead { position: sticky; top: 0; background: #F9FAFB; z-index: 10; }
        .data-table th { padding: 12px 24px; text-align: left; font-size: 12px; font-weight: 600; color: #64748B; text-transform: uppercase; border-bottom: 1px solid #E5E7EB; }
        .data-table td { padding: 14px 24px; font-size: 13px; color: #1A1A1A; border-bottom: 1px solid #F1F5F9; }
        .categoria-row:hover { background: #F9FAFB; }
        .subcategoria-row { background: #FAFAFA; }
        .subcategoria-row:hover { background: #F5F5F5; }
        .expand-btn { padding: 4px; border: none; background: transparent; color: #64748B; cursor: pointer; border-radius: 4px; transition: all 0.2s; }
        .expand-btn:hover:not(:disabled) { background: #F1F5F9; color: #8B5CF6; }
        .expand-btn:disabled { cursor: default; }
        .subcategoria-name { display: flex; align-items: center; gap: 8px; padding-left: 20px; }
        .subcategoria-name svg { color: #8B5CF6; }
        .font-medium { font-weight: 500; }
        .badge { display: inline-flex; align-items: center; justify-content: center; padding: 2px 8px; background: #EDE9FE; color: #8B5CF6; font-size: 11px; font-weight: 600; border-radius: 10px; }
        .badge.secondary { background: #EFF6FF; color: #3B82F6; }
        .action-buttons { display: flex; gap: 4px; }
        .btn-icon { padding: 6px; border: none; border-radius: 4px; background: transparent; cursor: pointer; transition: all 0.2s; }
        .btn-icon.add { color: #8B5CF6; }
        .btn-icon.add:hover { background: #EDE9FE; }
        .btn-icon.edit { color: #3B82F6; }
        .btn-icon.edit:hover { background: #EFF6FF; }
        .btn-icon.delete { color: #EF4444; }
        .btn-icon.delete:hover { background: #FEF2F2; }
        .empty-state { text-align: center; padding: 48px 24px !important; color: #94A3B8; }
        .empty-state svg { margin-bottom: 8px; }
        .empty-state span { display: block; font-size: 13px; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-box { background: white; border-radius: 8px; width: 100%; max-width: 480px; overflow: hidden; }
        .modal-box.confirm { max-width: 400px; text-align: center; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 20px; border-bottom: 1px solid #E5E7EB; }
        .modal-header h3 { margin: 0; font-size: 17px; font-weight: 600; }
        .modal-header button { padding: 5px; border: none; border-radius: 4px; background: none; color: #64748B; cursor: pointer; transition: all 0.2s; }
        .modal-header button:hover { background: #F1F5F9; color: #EF4444; }
        .modal-body { padding: 20px; display: flex; flex-direction: column; gap: 14px; }
        .form-group { display: flex; flex-direction: column; gap: 5px; }
        .form-group label { font-size: 12px; font-weight: 500; color: #64748B; }
        .required { color: #EF4444; }
        .input { padding: 8px 10px; border: 1px solid #E5E7EB; border-radius: 6px; font-size: 13px; color: #1A1A1A; background: white; transition: all 0.2s; }
        .input:focus { outline: none; border-color: #8B5CF6; box-shadow: 0 0 0 3px rgba(139,92,246,0.1); }
        .modal-footer { padding: 14px 20px; border-top: 1px solid #E5E7EB; display: flex; justify-content: flex-end; gap: 8px; }
        .confirm-icon { display: flex; justify-content: center; padding: 20px; color: #EF4444; }
        .modal-box.confirm h3 { margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1A1A1A; padding: 0 20px; }
        .modal-box.confirm p { margin: 0; padding: 0 20px 20px; font-size: 13px; color: #64748B; line-height: 1.5; }
        .confirm-actions { display: flex; gap: 8px; padding: 14px 20px; border-top: 1px solid #E5E7EB; justify-content: center; }
        .btn-danger { display: flex; align-items: center; gap: 6px; padding: 7px 14px; border: none; border-radius: 6px; background: #EF4444; color: white; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .btn-danger:hover { background: #DC2626; }
        @media (max-width: 768px) { .gestion-toolbar { flex-direction: column; gap: 12px; } .search-box { width: 100%; } }
      `}</style>
    </div>
  );
};

export default GestionCategorias;